import React, { useEffect } from 'react';
import { Box } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import { Txt } from '../design-system';
import { INK, MonoLabel, PanelButton } from '../instrument';
import ProOffer from '../pro/ProOffer';
import { useProPurchase, useProRecap } from '../pro/useProPurchase';
import { isNativeIOS } from '../../utils/capacitor';

interface TrialPaywallProps {
  firstName?: string;
  /** Where to go when purchases can't happen here (web): the Upgrade page. */
  onStartTrial: () => void;
  onSkip: () => void;
}

/**
 * The one-time offer the first time the app opens after the free Pro day ends.
 * Since 2026-11-03 it is the shared Pro offer (components/pro/ProOffer): the
 * person's own numbers, one plan choice with annual picked, and a store purchase
 * right here — before, "Start my free week" went to the Upgrade page and asked
 * again. A quiet link stays on the free version.
 */
const TrialPaywall: React.FC<TrialPaywallProps> = ({ firstName, onStartTrial, onSkip }) => {
  const iap = useProPurchase('trial_paywall');
  const recap = useProRecap();
  const name = firstName && firstName.toLowerCase() !== 'there' && /^[A-Za-z][A-Za-z'’-]+$/.test(firstName) ? firstName : null;

  // Bought: close the offer.
  useEffect(() => { if (iap.success) { const t = setTimeout(onSkip, 1200); return () => clearTimeout(t); } }, [iap.success, onSkip]);

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1400, background: INK.ground, overflowY: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, py: 3 }}>
      <Box sx={{ width: '100%', maxWidth: 460, m: 'auto' }}>
        <ProOffer
          src="trial_paywall"
          products={iap.products}
          supported={iap.supported}
          ready={iap.ready}
          loading={iap.loading}
          error={iap.error}
          success={iap.success}
          onPurchase={iap.purchase}
          onRestore={iap.restore}
          onUnsupported={onStartTrial}
          store={isNativeIOS() ? 'App Store' : 'Google Play'}
          recap={recap}
          intro={
            <Box sx={{ mb: 1.25 }}>
              <Box className="icon-text-row" sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
                <BoltIcon sx={{ fontSize: 18, color: INK.cyan, filter: `drop-shadow(0 0 6px ${INK.cyan})` }} />
                <MonoLabel>FuelTron Pro</MonoLabel>
              </Box>
              {/* Txt, not Typography: global.css forces body Typography to a small fixed size on phones. */}
              <Txt sx={{
                fontWeight: 800, letterSpacing: '-0.3px', fontSize: { xs: '1.5rem', sm: '1.9rem' }, lineHeight: 1.15, mb: 1,
                background: `linear-gradient(90deg, ${INK.cyan}, ${INK.green})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                filter: `drop-shadow(0 0 10px ${INK.cyan}40)`,
              }}>
                {name ? `${name}, your Pro day is over.` : 'Your Pro day is over.'}
              </Txt>
              <Txt sx={{ color: INK.soft, fontSize: '0.9rem', lineHeight: 1.45 }}>
                Keep photo logging and everything else you used yesterday with a <b style={{ color: '#fff' }}>free week</b>. No charge today.
              </Txt>
            </Box>
          }
        />
        <PanelButton variant="quiet" onClick={onSkip} fullWidth sx={{ mt: 1, textDecoration: 'underline' }}>
          Continue with the free version
        </PanelButton>
      </Box>
    </Box>
  );
};

export default TrialPaywall;
